import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import { StoreInterface } from '../store/types';
import { DeliveryMode, ORDER_METHODS } from '../services/exports/Constants';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import moment from 'moment';
import useOrderMethodsStatus from '../hooks/availability/useOrderMethodsStatus';
import DeliveryQuotesManager from '../services/api/DeliveryQuotesManager';
import useDeepRef from '../hooks/utility/useDeepRef';
import useOrderSlots from '../hooks/availability/useOrderSlots';
import useTableOrder from '../hooks/order/useTableOrder';
import { TABLE_SELECTION_TTL_MS, TableLinkNotice, isTableSelectionActive } from '../utils/on-site';
import { selectTableNotice, setTableNotice } from '../store/slices/OrderMethodSlice';

/** How often a table order whose visit ran out mid-payment is checked again, once nothing holds it. */
const TABLE_VISIT_RECHECK_MS = 30_000;

export const SideEffectsContext = createContext<{
  loading: boolean;
  setLoading: (loading: boolean) => void;
  /**
   * Holds the table's visit check while a payment is in flight, so the order isn't turned into pickup
   * under the guest; returns the release. The backend has no expiry on a table, so the check runs as
   * soon as the last hold is released.
   */
  holdTableVisitCheck: () => () => void;
  /**
   * Where /checkout and /payment send the guest when the table's visit runs out there: the menu, which
   * says only a new scan returns to the table. The latest screen wins; returns the unregister. A visit
   * that ran out before the screen opened, with the guest not yet sent to the menu or shown its note
   * there, sends the guest to the menu as soon as the screen registers; once per visit's end, so it
   * can't loop.
   */
  registerTableVisitExit: (exit: () => void) => () => void;
  /** The menu has shown the guest that the visit ended: /checkout and /payment no longer send them back. */
  markTableVisitEndSeen: () => void;
}>({
  loading: false,
  setLoading: () => {},
  holdTableVisitCheck: () => () => {},
  registerTableVisitExit: () => () => {},
  markTableVisitEndSeen: () => {},
});

const SideEffectsProvider = ({ children }: PropsWithChildren) => {
  const { company } = useSelector((state: StoreInterface) => state?.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { profile } = useSelector((state: StoreInterface) => state.profile);
  const { isAtTable } = useTableOrder();
  const store = useStore<StoreInterface>();
  const { enabledOrderMethods } = useOrderMethodsStatus();
  const [loading, setLoading] = useState<boolean>(false);
  const tableVisitHoldsRef = useRef(new Set<symbol>());
  const tableVisitExitsRef = useRef<(() => void)[]>([]);
  // Whether the guest was sent to the menu, or shown the menu's note, since the visit ended
  // (`tableNotice` is VisitEnded). Until then /checkout and /payment send the guest to the menu.
  const tableVisitEndSeenRef = useRef(false);
  const tableNotice = useSelector(selectTableNotice);
  const runTableVisitCheckRef = useRef<() => void>(() => {});
  const scheduledTimeRef = useDeepRef(cached_order?.scheduled_for);
  const loadingRef = useDeepRef(loading);
  const currentDate = moment();
  const disabled =
    company?.has_asap_orders ||
    !cached_order?.scheduled_for ||
    !(
      (!company?.has_pre_order && !moment(cached_order?.scheduled_for).isSame(moment(), 'day')) ||
      moment(cached_order?.scheduled_for).isBefore(moment(), 'day')
    );
  const { orderSlots, isLoading: isSlotsLoading } = useOrderSlots({
    orderMethod: cached_order?.method,
    date: currentDate,
    disabled,
  });

  /**
   * If cached order method is disabled - reset to the first enabled one.
   * Prefer non-delivery so we don't auto-draft an incomplete delivery; if delivery
   * is the only enabled method, land on it — OrderFlowProvider.draftOrder guards
   * the auto-draft with isDeliveryComplete, and the user completes the address
   * via the order method modal.
   */
  useEffect(() => {
    // The live cart, not this render's: a table's link sets table ordering in the same commit as the
    // company it loaded, and its effect runs before this one.
    const method = store.getState().order.cached_order?.method;

    if (!enabledOrderMethods.length || enabledOrderMethods.includes(method)) {
      return;
    }

    // Table ordering is never a fallback: only a table's QR code starts it.
    const candidates = enabledOrderMethods.filter((method) => method !== ORDER_METHODS.ON_SITE);

    if (!candidates.length) {
      return;
    }

    const nonDelivery = candidates.find((method) => method !== ORDER_METHODS.DELIVERY);
    const next = nonDelivery ?? candidates[0];

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        method: next,
      }),
    );

    // A guest at a table whose restaurant stopped table ordering: the menu says why it is pickup now.
    if (method === ORDER_METHODS.ON_SITE) {
      ReduxActions.dispatch(setTableNotice(TableLinkNotice.TableOrderingOff));
    }
  }, [enabledOrderMethods, cached_order?.method]);

  // A table order needs this visit's table at this restaurant; without it (expired, another location)
  // the cart goes back to pickup. Waits for the company, which the table is checked against.
  // The live store, for the same reason as above: a fresh scan at another location is not undone.
  // The visit runs out at a set time, so a timer checks then too, not only on the next change.
  // The backend has no expiry on a table: an expired one is only ever dropped here, on /checkout and
  // /payment too (a tab restored there the next day would otherwise pay to yesterday's table). Only a
  // payment in flight holds the check, and its release runs it.
  useEffect(() => {
    let recheck: ReturnType<typeof setInterval>;

    // 'deferred': the table no longer applies, but a payment is in flight; checked again on release.
    const check = (): 'kept' | 'deferred' | 'left' => {
      const { order, initialData } = store.getState();
      const companyId = initialData.company?.id;
      const cachedOrder = order.cached_order;
      const table = cachedOrder?.on_site_table;

      if (!companyId || cachedOrder?.method !== ORDER_METHODS.ON_SITE || isTableSelectionActive(table, companyId)) {
        return 'kept';
      }

      if (tableVisitHoldsRef.current.size > 0) {
        return 'deferred';
      }

      // This restaurant's table, run out: dropped, and only a new scan returns to it. At another
      // location the table is still good at its own, so "Eating here? Order to Table 7" there is the
      // way back, not a scan.
      const visitEnded = table?.company_id === companyId;

      ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          method: ORDER_METHODS.PICKUP,
          ...(visitEnded && { on_site_table: null }),
        }),
      );

      if (visitEnded) {
        ReduxActions.dispatch(setTableNotice(TableLinkNotice.VisitEnded));
      }

      // On /checkout or /payment: back to the menu, which says why, instead of checking out a pickup
      // order the guest didn't choose. Anywhere else (e.g. /order/{uuid}, where a cancelled redirect
      // payment returns to), the next /checkout or /payment does it when it opens.
      const exit = tableVisitExitsRef.current[tableVisitExitsRef.current.length - 1];

      if (exit && visitEnded) {
        tableVisitEndSeenRef.current = true;
      }

      exit?.();

      return 'left';
    };

    const run = (): void => {
      if (check() === 'deferred' && !recheck) {
        recheck = setInterval(check, TABLE_VISIT_RECHECK_MS);
      }
    };

    runTableVisitCheckRef.current = run;
    run();

    const scannedAt = Date.parse(cached_order?.on_site_table?.scanned_at);

    if (cached_order?.method !== ORDER_METHODS.ON_SITE || !Number.isFinite(scannedAt)) {
      return () => clearInterval(recheck);
    }

    const timer = setTimeout(run, Math.max(scannedAt + TABLE_SELECTION_TTL_MS - Date.now(), 0) + 1);

    return () => {
      clearTimeout(timer);
      clearInterval(recheck);
    };
  }, [company?.id, cached_order?.method, cached_order?.on_site_table?.scanned_at, isAtTable]);

  const holdTableVisitCheck = useCallback(() => {
    const hold = Symbol('payment in flight');
    tableVisitHoldsRef.current.add(hold);

    return () => {
      if (!tableVisitHoldsRef.current.delete(hold) || tableVisitHoldsRef.current.size > 0) {
        return;
      }

      runTableVisitCheckRef.current();
    };
  }, []);

  // A new end of the visit is a new note: only the notice leaving VisitEnded (a scan, a chosen order
  // type) re-arms the exit, so the guest is sent to the menu once per end, never in a loop.
  useEffect(() => {
    if (tableNotice !== TableLinkNotice.VisitEnded) {
      tableVisitEndSeenRef.current = false;
    }
  }, [tableNotice]);

  const registerTableVisitExit = useCallback((exit: () => void) => {
    tableVisitExitsRef.current = [...tableVisitExitsRef.current, exit];

    // The visit ended before this screen opened, away from the menu: e.g. it ran out on /order/{uuid}
    // and a cancelled redirect payment brought the guest back to /payment, now with a pickup cart.
    if (selectTableNotice(store.getState()) === TableLinkNotice.VisitEnded && !tableVisitEndSeenRef.current) {
      tableVisitEndSeenRef.current = true;
      exit();
    }

    return () => {
      tableVisitExitsRef.current = tableVisitExitsRef.current.filter((registered) => registered !== exit);
    };
  }, []);

  const markTableVisitEndSeen = useCallback(() => {
    tableVisitEndSeenRef.current = true;
  }, []);

  const resetScheduledTime = useCallback(async () => {
    if (company?.has_asap_orders) {
      return ReduxActions.dispatch(actionCreators.order.resetScheduledTime());
    }

    if (isSlotsLoading || !orderSlots?.length) {
      return;
    }

    const newTime = orderSlots[0].start_time ?? null;

    ReduxActions.dispatch(
      actionCreators.order.resetScheduledTime({
        scheduled_for: newTime,
      }),
    );

    if (company?.delivery_mode !== DeliveryMode.Internal && !!cached_order?.delivery_quote?.id) {
      await DeliveryQuotesManager.refresh(cached_order.delivery_quote.id, { dropoff_time: newTime });
    }
  }, [
    company?.has_asap_orders,
    company?.id,
    company?.delivery_mode,
    cached_order?.method,
    cached_order?.delivery_quote?.id,
  ]);

  /**
   * Reset order schedule date if
   * - pre order is disabled and cached schedule date is set for other day than now
   * - cached order date is set to the past
   */
  useEffect(() => {
    if (!cached_order?.scheduled_for) {
      return;
    }

    if (
      (!company?.has_pre_order && !moment(cached_order?.scheduled_for).isSame(moment(), 'day')) ||
      moment(cached_order?.scheduled_for).isBefore(moment(), 'day')
    ) {
      resetScheduledTime();
    }
  }, [company?.has_pre_order, isSlotsLoading]);

  /**
   * Cache order delivery coordinates to local state from profile
   */
  useEffect(() => {
    cached_order?.method === ORDER_METHODS.DELIVERY &&
      profile?.delivery_lat &&
      profile?.delivery_lng &&
      !cached_order?.delivery_lat &&
      !cached_order?.delivery_lng &&
      ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          delivery_lat: profile?.delivery_lat,
          delivery_lng: profile?.delivery_lng,
        }),
      );
  }, []);

  /**
   * Refresh expired delivery quote
   */
  useEffect(() => {
    const refreshDeliveryQuote = async () => {
      return (
        company?.delivery_mode !== DeliveryMode.Internal &&
        !!cached_order?.delivery_quote?.id &&
        !!cached_order?.delivery_quote?.expires_at &&
        moment(cached_order?.delivery_quote?.expires_at).isBefore(moment()) &&
        DeliveryQuotesManager.refresh(cached_order.delivery_quote.id, {
          dropoff_time: scheduledTimeRef.current ?? null,
        })
      );
    };

    refreshDeliveryQuote();
    const interval = setInterval(() => refreshDeliveryQuote(), 30000);

    return () => clearInterval(interval);
  }, [cached_order?.delivery_quote?.id, cached_order?.delivery_quote?.expires_at]);

  /**
   * Reset delivery_quote on location switch
   */
  useEffect(() => {
    if (!cached_order?.delivery_quote?.company_id || loadingRef.current) {
      return;
    }

    if (company?.id === cached_order?.delivery_quote?.company_id) {
      return;
    }

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        delivery_quote: null,
      }),
    );
  }, [company?.id, cached_order?.delivery_quote?.company_id]);

  const context = useMemo(
    () => ({ loading, setLoading, holdTableVisitCheck, registerTableVisitExit, markTableVisitEndSeen }),
    [loading, holdTableVisitCheck, registerTableVisitExit, markTableVisitEndSeen],
  );

  return <SideEffectsContext.Provider value={context}>{children}</SideEffectsContext.Provider>;
};

export default SideEffectsProvider;
